// Lifecycle Stage journey (Figma) — the customer lifecycle rendered as a left-to-right row of
// stage boxes. All boxes are greyed by default; the single stage the classifier identified for the
// call glows gold. Default (all-grey) is shown before import, on Stop/Clear, and when the stage
// couldn't be identified.

import { setText } from '../lib/escape.js';

// Canonical stages, in funnel order — must mirror the stage-identifier classifier
// (apps/worker/src/skills/registry.ts → CLASSIFIER_RUBRICS['stage-identifier']).
export const LIFECYCLE_STAGES = [
  'Prospecting',
  'Qualification',
  'Discovery',
  'Business Case',
  'Technical Validation',
  'Commercial',
  'Onboarding',
  'Adoption',
  'Expansion',
] as const;

const norm = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ').trim();

export class LifecycleJourney {
  private boxes = new Map<string, HTMLElement>();

  constructor(private readonly root: HTMLElement) {
    this.build();
  }

  build(): void {
    this.root.innerHTML = '';
    this.boxes.clear();
    LIFECYCLE_STAGES.forEach((stage, i) => {
      const box = document.createElement('div');
      box.className = 'lc-box';
      setText(box, stage);
      this.root.appendChild(box);
      this.boxes.set(norm(stage), box);
      if (i < LIFECYCLE_STAGES.length - 1) {
        const arrow = document.createElement('span');
        arrow.className = 'lc-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        setText(arrow, '›');
        this.root.appendChild(arrow);
      }
    });
  }

  /** Highlight the identified stage in gold; pass null/unknown to grey every box. */
  highlight(stage: string | null | undefined): void {
    for (const b of this.boxes.values()) b.classList.remove('lc-active');
    if (!stage) return;
    const key = norm(stage);
    // Exact match first, then a lenient contains-match (e.g. "Customer Onboarding" → "Onboarding").
    const box =
      this.boxes.get(key) ??
      [...this.boxes.entries()].find(([k]) => k.includes(key) || key.includes(k))?.[1];
    box?.classList.add('lc-active');
  }

  reset(): void {
    this.highlight(null);
  }
}

// Estimate a call's duration in minutes. Cosmetic only (never feeds scoring) — returns null when the
// transcript carries no usable signal. Prefers an explicit "duration" header, then falls back to
// timecodes (VTT/SRT/Gong-style H:MM:SS or M:SS markers).
export function estimateDurationMin(transcript: string): number | null {
  // 1) Explicit metadata: a "…duration … N minutes / H hours M min" phrase. Anchored on the keyword
  //    (within one line) so we never pick up "a 30-minute call" or "I have 35" from the dialogue.
  const durPhrase = transcript.match(/duration\b[^\n]{0,40}/i)?.[0];
  if (durPhrase) {
    // 1a) An explicit clock RANGE, e.g. "Duration: 09:00:00 - 09:29:41" (or "09:00 - 09:30") → the exact
    //     end-minus-start span. This is the most precise signal when a header carries one.
    const range = durPhrase.match(/(\d{1,2}):([0-5]\d)(?::([0-5]\d))?\s*[-–—to]+\s*(\d{1,2}):([0-5]\d)(?::([0-5]\d))?/i);
    if (range) {
      const start = Number(range[1]) * 3600 + Number(range[2]) * 60 + (range[3] ? Number(range[3]) : 0);
      const end = Number(range[4]) * 3600 + Number(range[5]) * 60 + (range[6] ? Number(range[6]) : 0);
      const mins = Math.round((end - start) / 60);
      if (mins >= 1 && mins <= 24 * 60) return mins;
    }
    const h = durPhrase.match(/(\d{1,2})\s*(?:hours?|hrs?)\b/i);
    const mm = durPhrase.match(/(\d{1,3})\s*(?:minutes?|mins?)\b/i);
    const mins = (h ? Number(h[1]) * 60 : 0) + (mm ? Number(mm[1]) : 0);
    if (mins >= 1 && mins <= 24 * 60) return mins;
  }

  // 2) Fall back to timecodes.
  const re = /(?:(\d{1,2}):)?([0-5]?\d):([0-5]\d)(?:[.,]\d{1,3})?/g;
  const codes: { sec: number; hadHour: boolean }[] = [];
  for (const m of transcript.matchAll(re)) {
    const hadHour = m[1] !== undefined;
    const h = m[1] ? Number(m[1]) : 0;
    codes.push({ sec: h * 3600 + Number(m[2]) * 60 + Number(m[3]), hadHour });
  }
  if (codes.length < 3) return null; // too few timecodes to trust

  // Prefer absolute clock codes (H:MM:SS) when the transcript has enough of them. A transcript with real
  // "[2026-01-14 09:29:41]" timestamps AND spoken times in the dialogue ("hard stop at 9:30") mixes two
  // scales: the spoken "9:30" reads as 9m30s while the stamp reads as 9h29m, and the blended span blows
  // up (a 30-min call reported as ~560 min). Restricting to hour-bearing codes drops the spoken noise.
  const hourCodes = codes.filter((c) => c.hadHour);
  const pick = hourCodes.length >= 3 ? hourCodes : codes;
  const secs = pick.map((c) => c.sec);
  const spanSec = Math.max(...secs) - Math.min(...secs);
  if (spanSec <= 0 || spanSec > 86_400) return null; // zero-span or absurd (>24h) → not real timecodes

  // Ambiguity guard: a single-colon transcript (no hour field anywhere) whose M:SS reading implies an
  // implausibly fast cadence — many turns ~1s apart — is really using [HH:MM] minute markers (e.g.
  // "[00:34]" = 34 minutes, not 34 seconds). Reinterpret A:B as (A*60 + B) minutes; since every hour
  // field is 0, the numeric second-span already equals that minute-span.
  const noHours = pick.every((c) => !c.hadHour);
  const distinct = new Set(secs).size;
  const meanGapSec = spanSec / Math.max(1, distinct - 1);
  if (noHours && distinct >= 6 && meanGapSec < 4) {
    const mins = Math.round(spanSec);
    if (mins >= 1 && mins <= 24 * 60) return mins;
  }

  return Math.max(1, Math.round(spanSec / 60));
}
